import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, PackageCheck, ShoppingBag } from "lucide-react";
import { formatDkk, getVare } from "@/lib/site";
import { useCart } from "@/context/CartContext";
import { Reveal } from "@/components/Reveal";
import Reviews from "@/components/Reviews";

const SHIPPING_NOTES = {
  geckos: "Afhentning i Billund eller personlig levering (300 kr, inden for 120 km)",
  products: "Forsendelse, afhentning i Billund eller personlig levering",
  terrariums: "Kun afhentning: Fælleden 16, 7190 Billund",
};

const KV_RE = /^([^:]{2,22}):\s*(.+)$/;

function Description({ text }) {
  const lines = (text || "").split("\n").filter((l) => l.trim());
  const bullets = lines.filter((l) => KV_RE.test(l));
  const paragraphs = lines.filter((l) => !KV_RE.test(l));
  return (
    <div className="mt-8 space-y-4">
      {paragraphs.map((p, i) => (
        <p key={i} className="text-base leading-relaxed text-sage">
          {p}
        </p>
      ))}
      {bullets.length > 0 && (
        <ul data-testid="vare-specs" className="space-y-2 border border-white/10 bg-moss p-6">
          {bullets.map((l, i) => {
            const [, key, value] = l.match(KV_RE);
            return (
              <li key={i} className="flex justify-between gap-4 text-sm">
                <span className="font-mono text-[11px] uppercase tracking-[0.15em] text-pale/70">
                  {key}
                </span>
                <span className="text-right text-cream">{value}</span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default function VarePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { add } = useCart();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [variant, setVariant] = useState(null);

  useEffect(() => {
    setData(null);
    setActive(0);
    setVariant(null);
    getVare(slug).then(setData).catch(() => setData({ item: null }));
    window.__lenis?.scrollTo(0, { immediate: true });
  }, [slug]);

  useEffect(() => {
    if (data?.item) document.title = `${data.item.name} — GEKKOFUEL`;
  }, [data]);

  if (!data) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center pt-32">
        <Loader2 className="h-6 w-6 animate-spin text-forest" />
      </div>
    );
  }

  const item = data.item;
  if (!item) {
    return (
      <div className="px-6 pb-24 pt-36 lg:px-16">
        <p className="text-sage">Varen blev ikke fundet.</p>
        <button
          data-testid="vare-back-button"
          onClick={() => navigate("/")}
          className="mt-6 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-pale"
        >
          <ArrowLeft className="h-4 w-4" /> Tilbage til forsiden
        </button>
      </div>
    );
  }

  const images = item.images?.length ? item.images : item.image ? [item.image] : [];
  const soldOut =
    item.status === "Udsolgt" ||
    (data.collection !== "terrariums" && (item.inventory_qty ?? 1) <= 0);
  const isCustom = data.collection === "terrariums" && item.kind === "custom";
  const isProduct = data.collection === "products";
  const step = (d) => setActive((a) => (a + d + images.length) % images.length);

  return (
    <div data-testid="vare-page" className="mx-auto max-w-6xl px-6 pb-24 pt-36 lg:px-16">
      <button
        data-testid="vare-back-button"
        onClick={() => navigate(-1)}
        className="mb-10 flex items-center gap-2 font-mono text-xs uppercase tracking-[0.2em] text-sage transition-colors hover:text-cream"
      >
        <ArrowLeft className="h-4 w-4" /> Tilbage
      </button>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-2">
        <Reveal>
          <div
            className={`relative flex items-center justify-center overflow-hidden border border-white/10 ${
              isProduct ? "bg-[#EFEBE3] p-8" : "bg-moss"
            }`}
          >
            <img
              data-testid="vare-main-image"
              src={images[active]}
              alt={item.name}
              className={`max-h-[480px] w-full ${
                isProduct ? "object-contain mix-blend-multiply" : "object-cover"
              }`}
            />
            {images.length > 1 && (
              <>
                <button
                  data-testid="vare-prev"
                  onClick={() => step(-1)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-ink/60 p-2 text-cream backdrop-blur-md transition-colors hover:bg-forest"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  data-testid="vare-next"
                  onClick={() => step(1)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full border border-white/20 bg-ink/60 p-2 text-cream backdrop-blur-md transition-colors hover:bg-forest"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </>
            )}
          </div>
          {images.length > 1 && (
            <div className="mt-4 flex gap-3">
              {images.map((src, i) => (
                <button
                  key={src}
                  data-testid={`vare-thumb-${i}`}
                  onClick={() => setActive(i)}
                  className={`h-20 w-20 overflow-hidden border transition-colors ${
                    active === i ? "border-forest" : "border-white/10 hover:border-white/40"
                  }`}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </Reveal>

        <Reveal delay={0.1}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-pale/70">
            {item.category || (data.collection === "geckos" ? "Correlophus ciliatus" : "Terrarium")}
          </p>
          <h1 className="mt-4 font-serif text-4xl tracking-tight text-cream sm:text-5xl">
            {item.name}
          </h1>
          <p data-testid="vare-price" className="mt-4 font-serif text-2xl text-pale">
            {variant
              ? formatDkk(item.variants.find((v) => v.opt === variant)?.price_dkk || item.price_dkk)
              : item.price}
          </p>
          <p
            data-testid="vare-status"
            className={`mt-3 font-mono text-[10px] uppercase tracking-[0.25em] ${
              soldOut ? "text-bark" : "text-pale"
            }`}
          >
            {soldOut
              ? "Udsolgt"
              : (item.inventory_qty ?? 0) > 0 && data.collection !== "terrariums"
                ? `På lager: ${item.inventory_qty} stk`
                : item.status || "På lager"}
          </p>

          <Description text={item.description} />

          {item.variants?.length > 0 && (
            <div className="mt-8 border-t border-white/10 pt-6">
              <div
                data-testid="vare-exoterra-badge"
                className="mb-6 flex items-center gap-3 border border-forest/50 bg-forest/10 p-4"
              >
                <PackageCheck className="h-5 w-5 shrink-0 text-pale" />
                <p className="text-sm leading-relaxed text-cream">
                  <span className="font-semibold">Inkluderet i prisen:</span> Exo
                  Terra-terrarie 45×45×60 cm — I betaler kun for de tilvalg, I klikker på
                  herunder.
                </p>
              </div>
              <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.25em] text-pale/70">
                Varianter — klik for at vælge
              </p>
              {item.variants.map((v) => (
                <button
                  key={v.opt}
                  data-testid={`variant-${v.opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setVariant(variant === v.opt ? null : v.opt)}
                  className={`flex w-full items-center justify-between border px-4 py-3 text-sm transition-colors duration-300 ${
                    variant === v.opt
                      ? "border-forest bg-forest/10"
                      : "border-white/10 hover:border-white/40"
                  }`}
                >
                  <span className="flex items-center gap-3 text-cream">
                    <span
                      className={`flex h-4 w-4 items-center justify-center rounded-full border ${
                        variant === v.opt ? "border-forest bg-forest" : "border-white/30"
                      }`}
                    >
                      {variant === v.opt && (
                        <span className="h-1.5 w-1.5 rounded-full bg-cream" />
                      )}
                    </span>
                    {v.opt}
                  </span>
                  <span className="font-mono text-xs text-pale">{formatDkk(v.price_dkk)}</span>
                </button>
              ))}
            </div>
          )}

          <p className="mt-8 font-mono text-[10px] uppercase leading-relaxed tracking-[0.2em] text-sage/70">
            {SHIPPING_NOTES[data.collection]}
          </p>

          {isCustom ? (
            <button
              data-testid="vare-enquire-button"
              onClick={() => {
                navigate("/");
                setTimeout(
                  () => window.__lenis?.scrollTo("#enquiry", { offset: -64 }),
                  450
                );
              }}
              className="mt-8 flex items-center gap-3 rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
            >
              Send forespørgsel
            </button>
          ) : soldOut ? (
            <p
              data-testid="vare-soldout"
              className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-sage"
            >
              Udsolgt
            </p>
          ) : (
            <button
              data-testid="vare-add-button"
              onClick={() => {
                if (item.variants?.length > 0 && !variant) {
                  toast.error("Vælg en variant først");
                  return;
                }
                const chosen = variant
                  ? item.variants.find((v) => v.opt === variant)
                  : null;
                add({
                  slug: item.slug,
                  name: item.name,
                  variant: variant || undefined,
                  price_dkk: chosen ? chosen.price_dkk : item.price_dkk,
                  image: images[0],
                  weight_kg: item.weight_kg,
                  no_postal: item.no_postal || undefined,
                });
              }}
              className="mt-8 flex items-center gap-3 rounded-full bg-forest px-7 py-3.5 font-mono text-xs uppercase tracking-[0.2em] text-cream transition-colors duration-300 hover:bg-pale hover:text-ink"
            >
              <ShoppingBag className="h-4 w-4" />
              Læg i kurv
            </button>
          )}
        </Reveal>
      </div>

      <Reviews slug={item.slug} />
    </div>
  );
}
